import React, { useEffect, useState, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { YearCalendar } from "@/components/YearCalendar";
import { Roadmap } from "@/components/Roadmap";
import { Plane, ShoppingBag, Flower2, Sparkles, Trash2, MapPin, Bookmark, Wallet, Lightbulb, CalendarDays, Bell, Clock } from "lucide-react";

const TYPE_META = {
  destination: { label: "Destinations", icon: MapPin },
  trip: { label: "Trips", icon: Plane },
  fashion: { label: "Fashion", icon: ShoppingBag },
  "self-care": { label: "Self-Care", icon: Flower2 },
  plan: { label: "Concierge Plans", icon: Sparkles },
};

const Dashboard = () => {
  const { user, loading, setAuthOpen, setPlannerOpen } = useApp();
  const [items, setItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [activePlan, setActivePlan] = useState(null);
  const [eventForm, setEventForm] = useState(null); // {date,title,note,time,remind}
  const year = new Date().getFullYear();

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([api.get("/saved"), api.get("/calendar")]);
      setItems(s.data);
      setEvents(c.data);
    } catch (e) {} finally { setFetching(false); }
  }, []);

  useEffect(() => {
    if (user) load();
    else if (!loading) setFetching(false);
  }, [user, loading, load]);

  const remove = async (id) => {
    try { await api.delete(`/saved/${id}`); setItems((p) => p.filter((i) => i.id !== id)); toast.success("Removed"); } catch (e) { toast.error("Could not remove"); }
  };

  const saveEvent = async () => {
    if (!eventForm.title) return toast.error("Add a title");
    try {
      const res = await api.post("/calendar", eventForm);
      setEvents((p) => [...p, res.data]);
      setEventForm(null);
      toast.success("Business date added" + (res.data.remind ? " · reminder set" : ""));
    } catch (e) { toast.error("Could not save date"); }
  };

  const deleteEvent = async (id) => {
    try { await api.delete(`/calendar/${id}`); setEvents((p) => p.filter((e) => e.id !== id)); toast.success("Date removed"); } catch (e) {}
  };

  if (loading || (user && fetching)) {
    return <Layout><div className="min-h-[60vh] flex items-center justify-center"><Sparkles className="text-champagne animate-pulse" size={28} /></div></Layout>;
  }

  if (!user) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6" data-testid="dashboard-signin-prompt">
          <div className="w-14 h-14 rounded-full gold-border flex items-center justify-center text-champagne mb-6"><Bookmark size={22} /></div>
          <h2 className="font-serif text-3xl text-champagne">Your dashboard awaits</h2>
          <p className="text-slate-400 mt-3 max-w-sm">Sign in to view saved items, your calendar and concierge plans.</p>
          <Button onClick={() => setAuthOpen(true)} data-testid="dashboard-signin-btn" className="mt-8 rounded-full h-12 px-8 bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] text-[#1a1a12] font-medium">Sign In</Button>
        </div>
      </Layout>
    );
  }

  const grouped = items.reduce((acc, it) => { (acc[it.item_type] = acc[it.item_type] || []).push(it); return acc; }, {});
  const upcoming = [...events].sort((a, b) => a.date.localeCompare(b.date));
  const completed = { 1: events.length > 0, 2: items.some((i) => i.item_type === "plan"), 3: items.length > 0 };

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="dashboard">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-400/90 font-semibold mb-3">My Dashboard</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-white">Welcome back, {user.name?.split(" ")[0] || "Guest"}.</h1>
            <p className="text-slate-400 mt-3">{items.length} saved · {events.length} business date{events.length === 1 ? "" : "s"} on your calendar.</p>
          </div>
          <Button onClick={() => setPlannerOpen(true)} data-testid="dashboard-planner-btn" className="rounded-full h-12 px-7 bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] text-[#1a1a12] font-medium"><Sparkles size={18} className="mr-2" /> New Plan</Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-12 mb-16">
          <div className="lg:col-span-2"><YearCalendar year={year} events={events} onSelectDate={(d) => setEventForm({ date: d, title: "", note: "", time: "", remind: false })} /></div>
          <div className="space-y-10">
            <div>
              <div className="flex items-center gap-2 mb-5 text-champagne"><CalendarDays size={20} /><h2 className="font-serif text-2xl">Upcoming dates</h2></div>
              {upcoming.length === 0 ? <p className="text-sm text-slate-500">Tap a date on the calendar to add a business date.</p> : (
                <div className="space-y-3" data-testid="upcoming-events">
                  {upcoming.map((e) => (
                    <div key={e.id} className="rounded-xl border border-white/10 bg-[#101017] p-4 flex items-start justify-between" data-testid={`event-${e.id}`}>
                      <div>
                        <p className="text-white text-sm font-medium">{e.title}</p>
                        <p className="text-xs text-slate-400 mt-1 flex items-center gap-2"><span className="text-champagne">{e.date}</span>{e.time && <><Clock size={11} /> {e.time}</>}{e.remind && <Bell size={11} className="text-amber-400" />}</p>
                        {e.note && <p className="text-xs text-slate-500 mt-1">{e.note}</p>}
                      </div>
                      <button onClick={() => deleteEvent(e.id)} className="text-slate-500 hover:text-red-400" data-testid={`delete-event-${e.id}`}><Trash2 size={15} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Roadmap completed={completed} />
          </div>
        </div>

        {items.length === 0 && (
          <div className="rounded-3xl border border-white/10 bg-[#101017] p-16 text-center" data-testid="dashboard-empty">
            <p className="font-serif text-2xl text-champagne">Nothing saved yet</p>
            <p className="text-slate-400 mt-3">Browse destinations, trips and services — tap the bookmark to collect them here.</p>
          </div>
        )}

        <div className="space-y-14">
          {Object.entries(grouped).map(([type, list]) => {
            const meta = TYPE_META[type] || { label: type, icon: Bookmark };
            const Icon = meta.icon;
            return (
              <div key={type} data-testid={`dashboard-group-${type}`}>
                <div className="flex items-center gap-3 mb-6"><Icon className="text-champagne" size={20} /><h2 className="font-serif text-2xl text-white">{meta.label}</h2><span className="text-sm text-slate-500">({list.length})</span></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {list.map((it) => (
                    <div key={it.id} data-testid={`saved-item-${it.id}`} className="group rounded-2xl overflow-hidden border border-white/10 bg-[#101017] hover:border-amber-400/40 transition-colors duration-500">
                      {type === "plan" ? (
                        <button onClick={() => setActivePlan(it.meta?.plan)} className="w-full text-left p-6">
                          <div className="w-11 h-11 rounded-full gold-border flex items-center justify-center text-champagne mb-4"><Sparkles size={18} /></div>
                          <h3 className="font-serif text-lg text-white">{it.title}</h3>
                          <p className="text-sm text-slate-400 mt-1">{it.subtitle}</p>
                          <span className="inline-block mt-4 text-sm text-champagne">View itinerary →</span>
                        </button>
                      ) : (
                        <div className="relative h-48 overflow-hidden">
                          {it.image ? <img src={it.image} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /> : <div className="w-full h-full bg-[#15151d]" />}
                          <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b10] to-transparent" />
                          <div className="absolute bottom-4 left-4"><h3 className="font-serif text-lg text-white">{it.title}</h3><p className="text-sm text-slate-300">{it.subtitle}</p></div>
                        </div>
                      )}
                      <div className="flex items-center justify-between px-5 py-3 border-t border-white/5">
                        <span className="text-xs text-slate-500">{new Date(it.created_at).toLocaleDateString()}</span>
                        <button onClick={() => remove(it.id)} data-testid={`remove-item-${it.id}`} className="text-slate-500 hover:text-red-400 transition-colors duration-200"><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Add event dialog */}
      <Dialog open={!!eventForm} onOpenChange={(o) => !o && setEventForm(null)}>
        <DialogContent className="sm:max-w-md bg-[#0b0b10] border border-[rgba(212,175,55,0.2)] text-white" data-testid="event-dialog">
          <DialogTitle className="sr-only">Add business date</DialogTitle>
          <DialogDescription className="sr-only">Add a calendar event with optional reminder.</DialogDescription>
          {eventForm && (
            <div className="space-y-4">
              <div>
                <p className="text-[10px] tracking-[0.25em] text-amber-400/80 uppercase">Business date</p>
                <h3 className="font-serif text-2xl text-champagne mt-1">{eventForm.date}</h3>
              </div>
              <input value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} placeholder="Title (e.g. Client dinner)" data-testid="event-title-input" className="w-full bg-[#14141c] border border-amber-400/15 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none" />
              <input type="time" value={eventForm.time} onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })} data-testid="event-time-input" className="w-full bg-[#14141c] border border-amber-400/15 rounded-xl px-4 py-3 text-sm text-white focus:outline-none" />
              <textarea value={eventForm.note} onChange={(e) => setEventForm({ ...eventForm, note: e.target.value })} placeholder="Note (optional)" rows={3} data-testid="event-note-input" className="w-full bg-[#14141c] border border-amber-400/15 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none resize-none" />
              <label className="flex items-center gap-3 text-sm text-slate-300 cursor-pointer">
                <input type="checkbox" checked={eventForm.remind} onChange={(e) => setEventForm({ ...eventForm, remind: e.target.checked })} data-testid="event-remind-checkbox" className="w-4 h-4 accent-amber-400" />
                <span className="flex items-center gap-2"><Bell size={14} className="text-amber-400" /> Email me a reminder on this day</span>
              </label>
              <Button onClick={saveEvent} data-testid="event-save-btn" className="w-full rounded-full bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] text-[#1a1a12] font-medium hover:opacity-90">Add to calendar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Plan detail dialog */}
      <Dialog open={!!activePlan} onOpenChange={(o) => !o && setActivePlan(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto bg-[#0b0b10] border border-[rgba(212,175,55,0.2)] text-white" data-testid="plan-detail-modal">
          <DialogTitle className="sr-only">{activePlan?.title || "Concierge plan"}</DialogTitle>
          <DialogDescription className="sr-only">Saved concierge itinerary details.</DialogDescription>
          {activePlan && (
            <div className="space-y-5">
              <div>
                <p className="text-[10px] tracking-[0.25em] text-amber-400/80 uppercase">Concierge Plan</p>
                <h3 className="font-serif text-2xl text-champagne mt-1">{activePlan.title}</h3>
                <p className="text-slate-400 text-sm mt-2">{activePlan.summary}</p>
              </div>
              {(activePlan.days || []).map((d) => (
                <div key={d.day} className="rounded-xl bg-[#13131a] border border-amber-400/10 p-5">
                  <div className="flex items-center gap-3 mb-3"><span className="w-7 h-7 rounded-full bg-amber-400/15 text-champagne text-sm flex items-center justify-center font-medium">{d.day}</span><span className="font-serif text-lg text-white">{d.title}</span></div>
                  <ul className="space-y-3">{(d.activities || []).map((a, i) => (<li key={i} className="flex gap-4"><span className="text-xs text-amber-400/80 w-20 shrink-0 pt-0.5">{a.time}</span><div><p className="text-sm text-white">{a.name}</p><p className="text-xs text-slate-400 mt-0.5">{a.detail}</p></div></li>))}</ul>
                </div>
              ))}
              {activePlan.budget && (<div className="rounded-xl bg-[#13131a] border border-amber-400/10 p-5"><div className="flex items-center gap-2 mb-3 text-champagne"><Wallet size={16} /><span className="font-medium">Budget · {activePlan.budget.total}</span></div><div className="flex flex-wrap gap-x-6 gap-y-2">{(activePlan.budget.breakdown || []).map((b, i) => <span key={i} className="text-sm text-slate-400">{b.label}: <span className="text-slate-200">{b.amount}</span></span>)}</div></div>)}
              {activePlan.tips?.length > 0 && (<div className="rounded-xl bg-[#13131a] border border-amber-400/10 p-5"><div className="flex items-center gap-2 mb-3 text-champagne"><Lightbulb size={16} /><span className="font-medium">Concierge tips</span></div><ul className="space-y-2 list-disc list-inside text-sm text-slate-400">{activePlan.tips.map((t, i) => <li key={i}>{t}</li>)}</ul></div>)}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Dashboard;
